/**
 * Notification Service
 * API client for notification management
 */

import { apiClient } from './api-client';
import type {
  Notification,
  NotificationFilters,
  NotificationStats
} from '../types/notification';
import type { PaginationOptions, ApiResponse, PaginatedResponse } from '../types/tasks';

export const notificationService = {
  /**
   * Get all notifications with filters and pagination
   */
  async getAll(
    filters?: NotificationFilters,
    pagination?: PaginationOptions
  ): Promise<ApiResponse<{ notifications: Notification[]; pagination: PaginatedResponse<Notification>['pagination'] }>> {
    const params: Record<string, unknown> = {
      ...filters,
      ...pagination,
      types: filters?.types?.join(','),
      priorities: filters?.priorities?.join(',')
    };

    const response = await apiClient.get('/api/notifications', { params });
    return {
      success: response.data.success,
      data: {
        notifications: response.data.data,
        pagination: response.data.pagination
      },
      timestamp: response.data.timestamp || new Date().toISOString()
    };
  },

  /**
   * Get unread notification count
   */
  async getUnreadCount(): Promise<ApiResponse<{ count: number }>> {
    const response = await apiClient.get('/api/notifications/unread-count');
    return response.data;
  },

  /**
   * Get notification statistics
   */
  async getStats(): Promise<ApiResponse<{ stats: NotificationStats }>> {
    const response = await apiClient.get('/api/notifications/stats');
    return response.data;
  },

  /**
   * Get notification by ID
   */
  async getById(id: string): Promise<ApiResponse<{ notification: Notification }>> {
    const response = await apiClient.get(`/api/notifications/${id}`);
    return response.data;
  },

  /**
   * Mark notification as read
   */
  async markAsRead(id: string): Promise<ApiResponse<void>> {
    const response = await apiClient.post(`/api/notifications/${id}/read`);
    return response.data;
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<ApiResponse<{ count: number }>> {
    const response = await apiClient.post('/api/notifications/mark-all-read');
    return response.data;
  },

  /**
   * Delete notification
   */
  async delete(id: string): Promise<ApiResponse<void>> {
    const response = await apiClient.delete(`/api/notifications/${id}`);
    return response.data;
  }
};
